/**
 * CLASSIC LOOK (v1.70): the pre-Floodlight design from commit ea44aec, kept
 * for people/sites on the Classic UI style. Functionality must match the
 * Floodlight version (screens/PlansScreen.jsx); port behaviour changes here too.
 *
 * Plans — pricing, checkout, plan changes and billing management.
 *
 * Visual idea: "the edge line". One luminous indigo → green line (an EV curve)
 * draws itself across the hero; the recommended card carries the same
 * gradient as its border. Prices roll when Monthly ↔ Yearly flips. Everything
 * answers the pointer: headline letters lift, cards tilt with a rim light,
 * prices wipe green, CTAs pull toward the cursor, FAQ rows sweep. All motion
 * goes through utils/motion + components/motion and is off under reduced motion.
 *
 * Money paths (functions/billing/index.js):
 *   new subscriber      → createCheckoutSession → Stripe Checkout → back here
 *                         (?checkout=success&session_id=…) → confirmCheckoutSession
 *   existing subscriber → previewPlanChange → confirm → changeSubscriptionPlan
 *   manage / cancel     → createBillingPortalSession, resumeSubscription
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, Platform, useWindowDimensions, ActivityIndicator, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import NavigationHeader from '../../components/NavigationHeader';
import { ScreenBackground } from '../../components/ThemeGradients';
import { useNavigation } from '../../context/NavigationContext';
import CheckoutTerms from '../../components/CheckoutTerms';
import { useTicker } from '../../context/TickerContext';
import { useAuth } from '../../context/AuthContext';
import Appear from '../../components/motion/Appear';
import PressableScale from '../../components/motion/PressableScale';
import SegmentedControl from '../../components/motion/SegmentedControl';
import AnimatedNumber from '../../components/motion/AnimatedNumber';
import Collapse from '../../components/motion/Collapse';
import EdgeLine from '../../components/motion/EdgeLine';
import Reveal from '../../components/motion/Reveal';
import Spotlight from '../../components/motion/Spotlight';
import AuroraFrame from '../../components/motion/AuroraFrame';
import KineticHeadline from '../../components/motion/KineticHeadline';
import AmbientGlow from '../../components/motion/AmbientGlow';
import MagneticButton from '../../components/motion/MagneticButton';
import CursorAura from '../../components/motion/CursorAura';
import Grain from '../../components/motion/Grain';
import KineticBand from '../../components/motion/KineticBand';
import { webTransition, IS_WEB } from '../../utils/motion';
import injectCss from '../../utils/injectCss';
import { showToast } from '../../utils/toast';
import {
    subscribeToPlanCatalog, startCheckout, confirmCheckout, previewPlanChange, changePlan,
    openBillingPortal, resumeSubscription,
} from '../../services/stripeService';
import { planList, displayFeatures, planAction, effectivePlan, hasStripeSubscription, formatPrice, yearlySavings, limitLabel } from '../../utils/billingPlans';

const C = {
    ink: 'rgba(255,255,255,0.94)',
    soft: 'rgba(255,255,255,0.68)',
    faint: 'rgba(255,255,255,0.42)',
    line: 'rgba(255,255,255,0.09)',
    card: 'rgba(22,22,34,0.92)',
    indigo: '#6366F1',
    violet: '#8B5CF6',
    green: '#34D399',
    warn: '#FBBF24',
    bad: '#F87171',
};
const DISPLAY = 'ClimateCrisis';
const UI = Platform.OS === 'web' ? 'Slate, -apple-system, system-ui, "Segoe UI", sans-serif' : undefined;

const INCLUDES = [
    ['analytics-outline', 'Fair odds on every price', 'Devigged against the full market, not one book.'],
    ['git-compare-outline', 'Every book side by side', 'The best number for each line, with how many books agree.'],
    ['layers-outline', 'Same-game parlays, optimized', 'Legs ranked by combined expected value, priced where you can bet them.'],
];

// What the kinetic band spells out (the same facts as the includes below).
const BAND = ['Fair odds', 'Expected value', 'Best book', 'Same-game parlays'];

// Plans-only hover details. Fine pointers only; off under reduced motion
// (Spotlight drops its data attribute then, so none of these match).
const HOVER_CSS = `
[data-feat], [data-feat-icon], [data-include-num], [data-include-icon], [data-faq-q], [data-faq-icon] { transition: transform 560ms cubic-bezier(0.34,1.4,0.64,1), color 300ms, background-color 260ms, border-color 260ms; }
${[1, 2, 3, 4, 5, 6, 7].map((i) => `[data-feat="${i}"] { transition-delay: ${i * 35}ms; }`).join('\n')}
[data-price-num] { -webkit-text-fill-color: transparent; -webkit-background-clip: text; background-clip: text;
  background-image: linear-gradient(90deg, #34D399 0 50%, rgba(255,255,255,0.94) 50% 100%); background-size: 200% 100%; background-position: 100% 0;
  transition: background-position 760ms cubic-bezier(0.7,0,0.2,1); }
[data-include-line] { transform: scaleX(0); transform-origin: left; transition: transform 760ms cubic-bezier(0.16,1,0.3,1); }
[data-faq-row] { position: relative; }
[data-faq-row]::before { content: ''; position: absolute; inset: 0 -14px; border-radius: 14px; pointer-events: none; transform: scaleX(0); transform-origin: left;
  background: linear-gradient(90deg, rgba(99,102,241,0.14), rgba(52,211,153,0.05) 60%, transparent); transition: transform 640ms cubic-bezier(0.16,1,0.3,1); }
@media (hover: hover) and (pointer: fine) {
  [data-spotlight]:hover [data-feat] { transform: translateX(6px); }
  [data-spotlight]:hover [data-feat-icon] { transform: scale(1.3) rotate(-10deg); }
  [data-spotlight]:hover [data-price-num] { background-position: 0 0; }
  [data-spotlight]:hover [data-include-num] { transform: translate3d(0, -6px, 0); color: #34D399; }
  [data-spotlight]:hover [data-include-icon] { transform: rotate(-14deg) scale(1.25); }
  [data-spotlight]:hover [data-include-line] { transform: scaleX(1); }
  [data-faq-row]:hover::before { transform: scaleX(1); }
  [data-faq-row]:hover [data-faq-q] { transform: translateX(12px); }
  [data-faq-row]:hover [data-faq-icon] { transform: rotate(90deg); background-color: rgba(52,211,153,0.16); border-color: rgba(52,211,153,0.6); }
  [data-faq-row]:hover [data-faq-num] { color: #34D399; }
}
@media (prefers-reduced-motion: reduce) {
  [data-feat], [data-feat-icon], [data-include-num], [data-include-icon], [data-faq-q], [data-faq-icon], [data-price-num], [data-include-line], [data-faq-row]::before { transition: none !important; }
  [data-faq-row]:hover::before { transform: scaleX(0); }
  [data-faq-row]:hover [data-faq-q], [data-faq-row]:hover [data-faq-icon] { transform: none !important; }
}
@media (forced-colors: active) { [data-price-num] { -webkit-text-fill-color: currentColor; background: none; } }
`;

const FAQ = [
    { q: 'What counts as a search?', a: 'One search is one question you ask, like "nba player points" or "chiefs vs bills". Only searches that return results count against your limit.' },
    { q: 'When does my limit reset?', a: 'Monthly limits reset on the 1st of each month (UTC). Daily limits, if a plan has one, reset at midnight UTC.' },
    { q: 'Can I switch plans later?', a: 'Yes. Upgrades and downgrades apply right away and are prorated: you pay or are credited the difference for the rest of the billing period.' },
    { q: 'How do I cancel?', a: 'Open Manage billing on this page. Your plan stays active until the end of the period you already paid for, and you can resume any time before then.' },
    { q: 'Is my payment secure?', a: 'Checkout and billing run on Stripe. BetOptimized never sees or stores your card number.' },
];

function useColumns(width) {
    if (width >= 1180) return 4;
    if (width >= 700) return 2;
    return 1;
}

export default function PlansScreen() {
    if (IS_WEB) injectCss('plans-hover', HOVER_CSS);
    const { navigateTo } = useNavigation();
    const { user, userProfile } = useAuth();
    const { totalHeaderHeight } = useTicker();
    const { width } = useWindowDimensions();
    const cols = useColumns(width);
    const padX = width >= 1024 ? 48 : width >= 600 ? 28 : 16;

    const [catalog, setCatalog] = useState(null);
    const [billingInterval, setBillingInterval] = useState('month');
    const [busyKey, setBusyKey] = useState(null);
    const [banner, setBanner] = useState(null); // { tone, title, body }
    const [change, setChange] = useState(null); // { plan, interval, preview, loading, error }
    const [openFaq, setOpenFaq] = useState(null);
    const isAdmin = String(userProfile?.userType || '').toLowerCase() === 'admin';
    const isTester = isAdmin || userProfile?.billingTester === true;
    const [testMode, setTestMode] = useState(false);
    const mode = testMode && isTester ? 'test' : 'live';

    useEffect(() => subscribeToPlanCatalog((c) => setCatalog(c)), []);

    // Return from Stripe Checkout.
    useEffect(() => {
        if (Platform.OS !== 'web' || typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        const status = params.get('checkout') || (params.get('subscribed') === '1' ? 'success' : null);
        if (!status) return;
        const sessionId = params.get('session_id');
        const clean = () => { try { window.history.replaceState(null, '', window.location.pathname); } catch { /* ignore */ } };
        if (status === 'canceled') {
            setBanner({ tone: 'info', title: 'Checkout canceled', body: 'Nothing was charged. Pick a plan whenever you are ready.' });
            clean();
            return;
        }
        if (!user) return; // wait for auth, then confirm
        setBanner({ tone: 'pending', title: 'Confirming your subscription…', body: 'This takes a second.' });
        (async () => {
            try {
                const out = sessionId ? await confirmCheckout(sessionId) : null;
                const planName = out?.planType ? out.planType.charAt(0) + out.planType.slice(1).toLowerCase() : null;
                setBanner({ tone: 'success', title: planName ? `Welcome to ${planName}.` : 'You are subscribed.', body: 'Your new limits are live now. A receipt is on its way from Stripe.' });
            } catch (e) {
                setBanner({ tone: 'warn', title: 'Payment received, still syncing', body: 'Your plan will update within a minute. If it does not, contact support with your Stripe receipt.' });
            } finally {
                clean();
            }
        })();
    }, [user]);

    const plans = useMemo(() => planList(catalog), [catalog]);
    const current = effectivePlan(userProfile || {});
    const sub = userProfile?.subscription || {};
    const subscriber = hasStripeSubscription(userProfile);
    const maxSave = useMemo(() => Math.max(0, ...plans.filter((p) => p.key !== 'FREE').map((p) => yearlySavings(p).pct)), [plans]);

    const onAction = useCallback(async (plan, action) => {
        if (action.kind === 'signin') { navigateTo('Login', { redirectAfterLogin: 'Plans' }); return; }
        if (action.kind === 'current' && !user) { navigateTo('Register'); return; }
        if (action.kind === 'manage-resume') {
            setBusyKey(plan.key);
            try { await resumeSubscription(); showToast('Plan resumed. It will renew as normal.', 'success'); }
            catch (e) { showToast(e.message, 'error'); }
            finally { setBusyKey(null); }
            return;
        }
        if (action.kind === 'change') {
            setChange({ plan, interval: billingInterval, loading: true });
            try {
                const preview = await previewPlanChange(plan.key, billingInterval);
                setChange({ plan, interval: billingInterval, preview, loading: false });
            } catch (e) {
                setChange({ plan, interval: billingInterval, loading: false, error: e.message });
            }
            return;
        }
        if (action.kind === 'checkout') {
            setBusyKey(plan.key);
            try { await startCheckout({ planKey: plan.key, interval: billingInterval, mode }); }
            catch (e) { showToast(e.message, 'error'); setBusyKey(null); }
        }
    }, [billingInterval, mode, navigateTo, user]);

    const confirmChange = async () => {
        if (!change?.plan) return;
        setChange((c) => ({ ...c, applying: true }));
        try {
            await changePlan(change.plan.key, change.interval, change.preview?.prorationDate);
            setChange(null);
            showToast(`You're on ${change.plan.name} now.`, 'success');
        } catch (e) {
            setChange((c) => ({ ...c, applying: false, error: e.message }));
        }
    };

    const manage = async () => {
        setBusyKey('portal');
        try { await openBillingPortal(); } catch (e) { showToast(e.message, 'error'); setBusyKey(null); }
    };

    const cardWidth = cols === 1 ? '100%' : `${100 / cols}%`;

    return (
        <View style={styles.root}>
            <ScreenBackground />
            <Grain />
            <CursorAura />
            <NavigationHeader />
            <ScrollView
                style={styles.scroll}
                contentContainerStyle={{ paddingTop: (Platform.OS === 'web' ? totalHeaderHeight : 0) + 24, paddingBottom: 96 }}
                showsVerticalScrollIndicator={false}
            >
                {/* Hero */}
                <View style={[styles.hero, { paddingHorizontal: padX }]}>
                    <AmbientGlow />
                    <EdgeLine height={width >= 900 ? 170 : 120} style={styles.heroLine} opacity={0.55} />
                    <Appear>
                        <Text style={styles.eyebrow} accessibilityRole="text">PLANS</Text>
                    </Appear>
                    {/* Narrow screens get three balanced lines instead of a wrapped first line. */}
                    <KineticHeadline
                        lines={width < 520 ? ['Pay for', 'the edge,', 'not the noise.'] : ['Pay for the edge,', 'not the noise.']}
                        outline={[width < 520 ? 2 : 1]}
                        textStyle={[styles.title, { fontSize: width >= 1200 ? 76 : width >= 768 ? 56 : width >= 520 ? 38 : 30, lineHeight: width >= 1200 ? 78 : width >= 768 ? 60 : width >= 520 ? 42 : 34 }]}
                    />
                    <Appear index={2}>
                        <Text style={[styles.lede, { maxWidth: 620 }]}>
                            Every plan prices every bet against the whole market: fair odds, expected value and the best book for the number. Choose how much you search.
                        </Text>
                    </Appear>
                    <Appear index={3} style={styles.toggleRow}>
                        <SegmentedControl
                            accessibilityLabel="Billing period"
                            options={[{ value: 'month', label: 'Monthly' }, { value: 'year', label: 'Yearly' }]}
                            value={billingInterval}
                            onChange={setBillingInterval}
                            accent="rgba(52,211,153,0.18)"
                            accentBorder="rgba(52,211,153,0.55)"
                        />
                        {maxSave > 0 && (
                            <View style={[styles.savePill, billingInterval === 'year' && styles.savePillOn]}>
                                <Text style={[styles.savePillText, billingInterval === 'year' && { color: C.green }]}>Yearly saves up to {maxSave}%</Text>
                            </View>
                        )}
                    </Appear>
                </View>

                {/* Status */}
                <View style={{ paddingHorizontal: padX }}>
                    {banner && <StatusBanner banner={banner} onClose={() => setBanner(null)} />}
                    {user && subscriber && (
                        <Appear index={2}>
                            <SubscriptionSummary sub={sub} current={current} plans={plans} onManage={manage} busy={busyKey === 'portal'} />
                        </Appear>
                    )}
                </View>

                {/* Cards */}
                <View style={[styles.grid, { paddingHorizontal: padX - 8 }]} accessibilityRole="list">
                    {plans.map((plan, i) => {
                        const action = planAction({ plan, interval: billingInterval, userData: userProfile || {}, signedIn: !!user, mode });
                        return (
                            <View key={plan.key} style={{ width: cardWidth, padding: 8 }} accessibilityRole="listitem">
                                <Reveal index={i} distance={36} style={{ flex: 1 }}>
                                    <PlanCard
                                        plan={plan}
                                        interval={billingInterval}
                                        action={action}
                                        isCurrent={current === plan.key && !!user}
                                        busy={busyKey === plan.key}
                                        onPress={() => onAction(plan, action)}
                                        tall={cols > 1}
                                    />
                                </Reveal>
                            </View>
                        );
                    })}
                </View>
                <CheckoutTerms navigateTo={navigateTo} />

                {/* Kinetic band: hollow display type, hover a phrase to fill it */}
                <View style={styles.bandWrap}>
                    <KineticBand items={BAND} size={width >= 900 ? 72 : 44} />
                    <KineticBand items={BAND} reverse faint tilt={1.2} size={width >= 900 ? 40 : 26} duration={52} style={{ marginTop: -6 }} />
                </View>

                {/* Includes: every plan gets the same engine */}
                <View style={{ paddingHorizontal: padX, marginTop: 40 }}>
                    <Reveal>
                        <EdgeLine variant="flat" height={44} opacity={0.6} delay={200} />
                        <Text style={[styles.includesKicker, { marginTop: 18 }]}>IN EVERY PLAN</Text>
                    </Reveal>
                    <View style={[styles.includes, width < 700 && { flexDirection: 'column' }]}>
                        {INCLUDES.map(([icon, h, b], i) => (
                            <Reveal key={h} index={i} style={{ flex: 1 }}>
                                <Spotlight style={styles.includeItem} tilt glow="rgba(52,211,153,0.14)">
                                    <View style={styles.includeHead}>
                                        <Text style={styles.includeNum} aria-hidden {...(IS_WEB ? { dataSet: { includeNum: '' } } : {})}>{String(i + 1).padStart(2, '0')}</Text>
                                        <View {...(IS_WEB ? { dataSet: { includeIcon: '' } } : {})}>
                                            <Ionicons name={icon} size={22} color={C.green} accessibilityElementsHidden />
                                        </View>
                                    </View>
                                    <Text style={styles.includeTitle}>{h}</Text>
                                    <Text style={styles.includeBody}>{b}</Text>
                                    {IS_WEB && <View style={styles.includeLine} dataSet={{ includeLine: '' }} aria-hidden />}
                                </Spotlight>
                            </Reveal>
                        ))}
                    </View>
                </View>

                {/* FAQ */}
                <View style={{ paddingHorizontal: padX, marginTop: 80, alignItems: 'center' }}>
                    <Reveal style={{ width: '100%', maxWidth: 760 }}>
                        <Text style={styles.sectionTitle} accessibilityRole="header">Questions</Text>
                        {FAQ.map((f, i) => (
                            <View key={f.q} style={styles.faqItem} {...(IS_WEB ? { dataSet: { faqRow: '' } } : {})}>
                                <PressableScale
                                    onPress={() => setOpenFaq(openFaq === i ? null : i)}
                                    style={styles.faqQ}
                                    scaleTo={0.995}
                                    lift={0}
                                    accessibilityRole="button"
                                    aria-expanded={openFaq === i}
                                    accessibilityLabel={f.q}
                                >
                                    <Text style={styles.faqNum} aria-hidden {...(IS_WEB ? { dataSet: { faqNum: '' } } : {})}>{String(i + 1).padStart(2, '0')}</Text>
                                    <Text style={styles.faqQText} {...(IS_WEB ? { dataSet: { faqQ: '' } } : {})}>{f.q}</Text>
                                    <View style={styles.faqIcon} {...(IS_WEB ? { dataSet: { faqIcon: '' } } : {})}>
                                        <Ionicons name="add" size={18} color={openFaq === i ? C.green : C.soft} style={[{ transform: [{ rotate: openFaq === i ? '45deg' : '0deg' }] }, webTransition(['transform'], 'base')]} />
                                    </View>
                                </PressableScale>
                                <Collapse open={openFaq === i}>
                                    <Text style={styles.faqA}>{f.a}</Text>
                                </Collapse>
                            </View>
                        ))}
                    </Reveal>
                </View>

                {/* Closing call to action */}
                <Reveal style={{ paddingHorizontal: padX, marginTop: 88 }}>
                    <Spotlight style={styles.closer} lift={false}>
                        <AmbientGlow />
                        <EdgeLine height={width >= 900 ? 120 : 80} style={styles.closerLine} opacity={0.5} delay={150} />
                        <KineticHeadline
                            lines={['Find your edge.']}
                            enter={false}
                            level={2}
                            textStyle={[styles.closerTitle, { fontSize: width >= 900 ? 84 : width >= 520 ? 48 : 34, lineHeight: width >= 900 ? 88 : width >= 520 ? 52 : 38 }]}
                        />
                        <Text style={styles.closerBody}>{user ? 'Your plan works everywhere: search, the Optimizer and same-game parlays.' : 'Start free. No card needed, upgrade when the searches run out.'}</Text>
                        <View style={styles.closerCtas}>
                            <MagneticButton
                                label={user ? 'Open the Optimizer' : 'Create a free account'}
                                onPress={() => navigateTo(user ? 'SameGameParlay' : 'Register')}
                                style={{ minWidth: 240, minHeight: 54 }}
                            />
                            <MagneticButton
                                label="Why BetOptimized"
                                variant="ghost"
                                arrow={false}
                                onPress={() => navigateTo('Why')}
                                style={{ minWidth: 240, minHeight: 54 }}
                            />
                        </View>
                    </Spotlight>
                </Reveal>

                {isTester && (
                    <View style={{ alignItems: 'center', marginTop: 40 }}>
                        <PressableScale
                            onPress={() => setTestMode((t) => !t)}
                            style={[styles.testToggle, testMode && styles.testToggleOn]}
                            accessibilityRole="switch"
                            aria-checked={!!testMode}
                            accessibilityLabel="Stripe test mode for checkouts"
                        >
                            <Ionicons name={testMode ? 'flask' : 'flask-outline'} size={14} color={testMode ? C.warn : C.faint} />
                            <Text style={[styles.testToggleText, testMode && { color: C.warn }]}>{testMode ? 'Test mode on: use card 4242 4242 4242 4242' : 'Admin: use Stripe test mode'}</Text>
                        </PressableScale>
                    </View>
                )}
            </ScrollView>

            <PlanChangeSheet change={change} onClose={() => setChange(null)} onConfirm={confirmChange} />
        </View>
    );
}

function PlanCard({ plan, interval, action, isCurrent, busy, onPress, tall }) {
    const free = plan.key === 'FREE';
    const amount = Number(plan.prices?.[interval]?.amount) || 0;
    const save = yearlySavings(plan);
    const featured = !!plan.highlight;
    const primary = action.kind === 'checkout' || action.kind === 'change' || action.kind === 'signin' || action.kind === 'manage-resume';
    const busyLabel = action.kind === 'checkout' ? 'Opening checkout' : action.kind === 'manage-resume' ? 'Resuming' : undefined;

    const body = (
        <Spotlight style={[styles.card, featured && styles.cardFeatured, isCurrent && styles.cardCurrent, tall && { flex: 1 }]} tilt={!featured} lift={false} glow={featured ? 'rgba(52,211,153,0.16)' : undefined}>
            <View style={styles.cardHead}>
                <Text style={styles.planName} accessibilityRole="header" aria-level={3}>{plan.name}</Text>
                {featured && !isCurrent && <View style={styles.tag}><Text style={styles.tagText}>RECOMMENDED</Text></View>}
                {isCurrent && <View style={[styles.tag, styles.tagCurrent]}><Text style={[styles.tagText, { color: C.green }]}>YOUR PLAN</Text></View>}
            </View>
            <Text style={styles.tagline}>{plan.tagline}</Text>

            <View style={styles.priceRow} accessible accessibilityLabel={free ? 'Free' : `${formatPrice(amount)} per ${interval === 'year' ? 'year' : 'month'}`}>
                {free ? (
                    <Text style={styles.price} {...(IS_WEB ? { dataSet: { priceNum: '' } } : {})}>$0</Text>
                ) : (
                    <AnimatedNumber value={amount} format={(v) => formatPrice(Math.round(v))} style={styles.price} {...(IS_WEB ? { dataSet: { priceNum: '' } } : {})} />
                )}
                <Text style={styles.per}>{free ? 'forever' : interval === 'year' ? '/ year' : '/ month'}</Text>
            </View>
            <Text style={styles.priceNote}>
                {free ? 'No card needed.' : interval === 'year' ? `${formatPrice(Math.round(save.perMonth * 100) / 100)}/mo billed yearly · save ${formatPrice(save.saved)}` : `or ${formatPrice(plan.prices?.year?.amount || 0)}/yr, ${save.pct}% off`}
            </Text>

            <View style={styles.limit}>
                <Ionicons name="search" size={13} color={C.green} />
                <Text style={styles.limitText}>{limitLabel(plan)}</Text>
            </View>

            <View style={[styles.features, tall && { flex: 1 }]}>
                {displayFeatures(plan).map((f, i) => (
                    <View key={f} style={styles.featureRow} {...(IS_WEB ? { dataSet: { feat: String(Math.min(i, 7)) } } : {})}>
                        <View style={{ marginTop: 1 }} {...(IS_WEB ? { dataSet: { featIcon: '' } } : {})}>
                            <Ionicons name="checkmark" size={15} color={featured ? C.green : 'rgba(255,255,255,0.55)'} />
                        </View>
                        <Text style={styles.featureText}>{f}</Text>
                    </View>
                ))}
            </View>

            <MagneticButton
                label={action.label}
                onPress={onPress}
                disabled={action.disabled}
                busy={busy}
                busyLabel={busyLabel}
                variant={!primary ? 'ghost' : featured ? 'primary' : 'light'}
                arrow={primary}
                style={action.disabled && action.kind === 'current' ? { opacity: 0.75 } : undefined}
                accessibilityLabel={`${action.label}${free ? '' : `, ${plan.name}`}`}
            />
        </Spotlight>
    );

    if (!featured) return body;
    // Featured card: the edge-line gradient as a frame that slowly turns; the
    // whole framed card tilts (the inner card keeps its glow and rim light).
    return (
        <Spotlight tilt rim={false} glow="rgba(0,0,0,0)" style={[{ borderRadius: 21 }, tall && { flex: 1 }]}>
            <AuroraFrame radius={21} style={tall && { flex: 1 }}>
                {body}
            </AuroraFrame>
        </Spotlight>
    );
}

function StatusBanner({ banner, onClose }) {
    const tone = {
        success: { icon: 'checkmark-circle', color: C.green, bg: 'rgba(52,211,153,0.10)' },
        warn: { icon: 'time', color: C.warn, bg: 'rgba(251,191,36,0.10)' },
        info: { icon: 'information-circle', color: '#A5B4FC', bg: 'rgba(99,102,241,0.10)' },
        pending: { icon: 'sync', color: '#A5B4FC', bg: 'rgba(99,102,241,0.10)' },
    }[banner.tone] || {};
    return (
        <Appear from="scale">
            <View style={[styles.banner, { backgroundColor: tone.bg, borderColor: `${tone.color}55` }]} accessibilityLiveRegion="polite" role="status">
                {banner.tone === 'pending' ? <ActivityIndicator size="small" color={tone.color} /> : <Ionicons name={tone.icon} size={20} color={tone.color} />}
                <View style={{ flex: 1 }}>
                    <Text style={styles.bannerTitle}>{banner.title}</Text>
                    {!!banner.body && <Text style={styles.bannerBody}>{banner.body}</Text>}
                </View>
                {banner.tone !== 'pending' && (
                    <PressableScale onPress={onClose} style={styles.iconBtn} accessibilityRole="button" accessibilityLabel="Dismiss">
                        <Ionicons name="close" size={16} color={C.soft} />
                    </PressableScale>
                )}
            </View>
        </Appear>
    );
}

function SubscriptionSummary({ sub, current, plans, onManage, busy }) {
    const plan = plans.find((p) => p.key === current);
    const end = sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd) : null;
    const endText = end && !Number.isNaN(end.getTime()) ? end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : null;
    const status = String(sub.subscriptionStatus || '');
    const line = status === 'cancel_pending' ? `Ends ${endText || 'at the end of this period'}. You can resume before then.`
        : status === 'past_due' ? 'Your last payment failed. Update your card to keep your plan.'
            : endText ? `Renews ${endText}${sub.interval === 'year' ? ' (yearly)' : ''}.` : 'Active.';
    return (
        <View style={[styles.summary, status === 'past_due' && { borderColor: 'rgba(251,191,36,0.45)' }]}>
            <View style={{ flex: 1, minWidth: 220 }}>
                <Text style={styles.summaryKicker}>{sub.livemode === false ? 'TEST SUBSCRIPTION' : 'YOUR SUBSCRIPTION'}</Text>
                <Text style={styles.summaryTitle}>{plan?.name || current}</Text>
                <Text style={[styles.summaryLine, status === 'past_due' && { color: C.warn }]}>{line}</Text>
            </View>
            <PressableScale onPress={onManage} disabled={busy} style={[styles.cta, styles.ctaGhost, { minWidth: 180 }]} hoverStyle={styles.ctaGhostHover} accessibilityRole="button" accessibilityLabel="Manage billing, invoices and payment method">
                {busy ? <ActivityIndicator size="small" color="#fff" /> : <Text style={styles.ctaText}>{status === 'past_due' ? 'Update payment' : 'Manage billing'}</Text>}
            </PressableScale>
        </View>
    );
}

function PlanChangeSheet({ change, onClose, onConfirm }) {
    const open = !!change;
    const p = change?.preview;
    return (
        <Modal visible={open} transparent animationType="fade" onRequestClose={onClose}>
            <View style={styles.sheetBackdrop}>
                <Appear from="scale" style={styles.sheet}>
                    <Text style={styles.sheetTitle} accessibilityRole="header">Switch to {change?.plan?.name}{change?.interval === 'year' ? ' yearly' : ' monthly'}</Text>
                    {change?.loading && <View style={{ paddingVertical: 24 }}><ActivityIndicator color="#fff" /></View>}
                    {!change?.loading && p && (
                        <>
                            <Text style={styles.sheetBody}>
                                {p.amountDue > 0
                                    ? `You'll be charged ${formatPrice(p.amountDue.toFixed(2) * 1)} today for the rest of this billing period, then the new price on renewal.`
                                    : p.total < 0
                                        ? `Nothing is charged today. ${formatPrice(Math.abs(p.total).toFixed(2) * 1)} of unused time is credited to your next invoice.`
                                        : 'Nothing is charged today. The new price starts on your next renewal.'}
                            </Text>
                            <Text style={styles.sheetFine}>Your new limits apply right away.</Text>
                        </>
                    )}
                    {!!change?.error && <Text style={[styles.sheetBody, { color: C.bad }]}>{change.error}</Text>}
                    <View style={styles.sheetActions}>
                        <PressableScale onPress={onClose} style={[styles.cta, styles.ctaGhost, { flex: 1 }]} hoverStyle={styles.ctaGhostHover} accessibilityRole="button">
                            <Text style={styles.ctaText}>Not now</Text>
                        </PressableScale>
                        <PressableScale onPress={onConfirm} disabled={!p || change?.applying} style={[styles.cta, styles.ctaPrimary, { flex: 1 }]} hoverStyle={styles.ctaPrimaryHover} accessibilityRole="button">
                            {change?.applying ? <ActivityIndicator size="small" color="#0B0B12" /> : <Text style={[styles.ctaText, styles.ctaTextPrimary]}>Confirm switch</Text>}
                        </PressableScale>
                    </View>
                </Appear>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    closerCtas: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 12, marginTop: 10 },
    root: { flex: 1, backgroundColor: '#000', ...(Platform.OS === 'web' ? { minHeight: '100vh' } : {}) },
    scroll: { flex: 1 },
    hero: { alignItems: 'center', paddingTop: 32, paddingBottom: 28, position: 'relative' },
    heroLine: { position: 'absolute', left: 0, right: 0, top: 24 },
    eyebrow: { color: C.green, fontFamily: UI, fontSize: 12, fontWeight: '700', letterSpacing: 4, marginBottom: 16 },
    title: { color: C.ink, fontFamily: DISPLAY, textAlign: 'center', textTransform: 'uppercase', letterSpacing: -1 },
    lede: { color: C.soft, fontFamily: UI, fontSize: 17, lineHeight: 26, textAlign: 'center', marginTop: 20 },
    toggleRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 28, flexWrap: 'wrap', justifyContent: 'center' },
    savePill: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 999, borderWidth: 1, borderColor: C.line, ...webTransition(['border-color', 'background-color'], 'base') },
    savePillOn: { borderColor: 'rgba(52,211,153,0.5)', backgroundColor: 'rgba(52,211,153,0.08)' },
    savePillText: { color: C.faint, fontFamily: UI, fontSize: 12, fontWeight: '700' },

    grid: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'stretch', marginTop: 12, alignSelf: 'center', width: '100%', maxWidth: 1400 },
    card: { backgroundColor: C.card, borderRadius: 20, borderWidth: 1, borderColor: C.line, padding: 24, gap: 6 },
    cardFeatured: { borderWidth: 0, backgroundColor: 'rgba(18,18,30,0.97)' },
    cardCurrent: { borderColor: 'rgba(52,211,153,0.45)' },
    cardHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
    planName: { color: C.ink, fontFamily: DISPLAY, fontSize: 24, textTransform: 'uppercase', letterSpacing: 0.5 },
    tag: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, backgroundColor: 'rgba(139,92,246,0.16)', borderWidth: 1, borderColor: 'rgba(139,92,246,0.45)' },
    tagCurrent: { backgroundColor: 'rgba(52,211,153,0.12)', borderColor: 'rgba(52,211,153,0.45)' },
    tagText: { color: '#C4B5FD', fontFamily: UI, fontSize: 10, fontWeight: '800', letterSpacing: 1 },
    tagline: { color: C.soft, fontFamily: UI, fontSize: 14, lineHeight: 20, minHeight: 40 },
    priceRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 6, marginTop: 14 },
    price: { color: C.ink, fontFamily: UI, fontSize: 46, fontWeight: '800', letterSpacing: -1.5, lineHeight: 50 },
    per: { color: C.faint, fontFamily: UI, fontSize: 14, marginBottom: 8 },
    priceNote: { color: C.faint, fontFamily: UI, fontSize: 12, minHeight: 18 },
    limit: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 14, paddingVertical: 8, paddingHorizontal: 10, borderRadius: 10, backgroundColor: 'rgba(52,211,153,0.07)', alignSelf: 'flex-start' },
    limitText: { color: C.ink, fontFamily: UI, fontSize: 13, fontWeight: '700' },
    features: { gap: 10, marginTop: 16, marginBottom: 20 },
    featureRow: { flexDirection: 'row', gap: 8, alignItems: 'flex-start' },
    featureText: { color: 'rgba(255,255,255,0.8)', fontFamily: UI, fontSize: 14, lineHeight: 20, flex: 1 },
    cta: { minHeight: 46, borderRadius: 12, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 18 },
    ctaPrimary: { backgroundColor: '#F5F5FA' },
    ctaPrimaryHover: { backgroundColor: '#FFFFFF', ...(Platform.OS === 'web' ? { boxShadow: '0 8px 28px rgba(52,211,153,0.25)' } : {}) },
    ctaFeatured: { backgroundColor: C.green },
    ctaGhost: { backgroundColor: 'rgba(255,255,255,0.06)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.14)' },
    ctaGhostHover: { backgroundColor: 'rgba(255,255,255,0.10)', borderColor: 'rgba(255,255,255,0.24)' },
    ctaText: { color: C.ink, fontFamily: UI, fontSize: 15, fontWeight: '700' },
    ctaTextPrimary: { color: '#0B0B12' },

    banner: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 16, borderRadius: 14, borderWidth: 1, marginBottom: 16, alignSelf: 'center', width: '100%', maxWidth: 880 },
    bannerTitle: { color: C.ink, fontFamily: UI, fontSize: 15, fontWeight: '700' },
    bannerBody: { color: C.soft, fontFamily: UI, fontSize: 13, marginTop: 2 },
    iconBtn: { width: 32, height: 32, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
    summary: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 16, padding: 20, borderRadius: 16, borderWidth: 1, borderColor: 'rgba(52,211,153,0.3)', backgroundColor: 'rgba(52,211,153,0.05)', marginBottom: 16, alignSelf: 'center', width: '100%', maxWidth: 880 },
    summaryKicker: { color: C.green, fontFamily: UI, fontSize: 11, fontWeight: '800', letterSpacing: 2 },
    summaryTitle: { color: C.ink, fontFamily: DISPLAY, fontSize: 26, textTransform: 'uppercase', marginTop: 4 },
    summaryLine: { color: C.soft, fontFamily: UI, fontSize: 14, marginTop: 4 },

    includesKicker: { color: C.green, fontFamily: UI, fontSize: 12, fontWeight: '700', letterSpacing: 4, textAlign: 'center' },
    includes: { flexDirection: 'row', gap: 16, marginTop: 24, alignSelf: 'center', width: '100%', maxWidth: 1100 },
    includeItem: { flex: 1, gap: 8, padding: 24, minHeight: 190, borderRadius: 18, borderWidth: 1, borderColor: C.line, backgroundColor: 'rgba(255,255,255,0.02)', overflow: 'hidden' },
    includeHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
    includeNum: { color: 'rgba(255,255,255,0.16)', fontFamily: DISPLAY, fontSize: 40, letterSpacing: -0.5 },
    includeLine: { position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, backgroundColor: C.green, ...(Platform.OS === 'web' ? { backgroundImage: 'linear-gradient(90deg, #6366F1, #8B5CF6, #34D399)' } : {}) },
    bandWrap: { marginTop: 72, overflow: 'hidden', paddingVertical: 18 },
    includeTitle: { color: C.ink, fontFamily: UI, fontSize: 16, fontWeight: '700' },
    includeBody: { color: C.soft, fontFamily: UI, fontSize: 14, lineHeight: 21 },

    sectionTitle: { color: C.ink, fontFamily: DISPLAY, fontSize: 30, textTransform: 'uppercase', marginBottom: 16 },
    closer: { alignSelf: 'center', width: '100%', maxWidth: 1100, alignItems: 'center', gap: 12, paddingVertical: 56, paddingHorizontal: 24, borderRadius: 28, borderWidth: 1, borderColor: C.line, backgroundColor: 'rgba(14,14,24,0.85)', overflow: 'hidden' },
    closerLine: { position: 'absolute', left: 0, right: 0, top: 10 },
    closerTitle: { color: C.ink, fontFamily: DISPLAY, textTransform: 'uppercase', textAlign: 'center', letterSpacing: -0.5 },
    closerBody: { color: C.soft, fontFamily: UI, fontSize: 16, lineHeight: 24, textAlign: 'center', maxWidth: 520 },
    faqItem: { borderTopWidth: 1, borderTopColor: C.line },
    faqQ: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 20, gap: 16 },
    faqNum: { color: 'rgba(255,255,255,0.28)', fontFamily: UI, fontSize: 12, fontWeight: '800', letterSpacing: 1, width: 22 },
    faqQText: { color: C.ink, fontFamily: UI, fontSize: 17, fontWeight: '600', flex: 1 },
    faqIcon: { width: 34, height: 34, borderRadius: 17, borderWidth: 1, borderColor: C.line, alignItems: 'center', justifyContent: 'center' },
    faqA: { color: C.soft, fontFamily: UI, fontSize: 15, lineHeight: 23, paddingBottom: 20, paddingRight: 28, paddingLeft: 38 },

    testToggle: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999, borderWidth: 1, borderColor: C.line },
    testToggleOn: { borderColor: 'rgba(251,191,36,0.5)', backgroundColor: 'rgba(251,191,36,0.08)' },
    testToggleText: { color: C.faint, fontFamily: UI, fontSize: 12, fontWeight: '600' },

    sheetBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.65)', alignItems: 'center', justifyContent: 'center', padding: 16 },
    sheet: { width: '100%', maxWidth: 440, backgroundColor: '#14141F', borderRadius: 18, borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)', padding: 24, gap: 10 },
    sheetTitle: { color: C.ink, fontFamily: UI, fontSize: 20, fontWeight: '800' },
    sheetBody: { color: C.soft, fontFamily: UI, fontSize: 15, lineHeight: 22 },
    sheetFine: { color: C.faint, fontFamily: UI, fontSize: 13 },
    sheetActions: { flexDirection: 'row', gap: 10, marginTop: 10 },
});
